/**
 * useSiteSeo — fetches global SEO settings once per session and injects:
 *   - Organization JSON-LD (with sameAs from saved social profiles)
 *   - WebSite JSON-LD (with SearchAction)
 *   - Search-engine verification meta tags (Google, Bing, Pinterest, FB)
 *
 * Mounted once at the App root. Idempotent — re-renders refresh the same
 * head elements rather than duplicating them. Uses `data-fg-jsonld` /
 * `data-fg-verify` attributes so we never collide with the per-page
 * useSeo() hook (which uses `data-fg-jsonld="page"`).
 */
import { useEffect, useRef } from "react";
import { api } from "./api";
import { PROD_DOMAIN } from "./seo";

let _cache = null;

function setOrUpdate(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    const tag = selector.startsWith("script") ? "script" : "meta";
    el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
}

function injectVerificationMeta(name, value, key) {
  if (!value) return;
  // Prefer updating an existing tag (static one from public/index.html,
  // or a previously-injected one) over creating a duplicate.
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("data-fg-verify", key);
  el.setAttribute("content", value);
}

function injectJsonLd(slot, payload) {
  const sel = `script[data-fg-jsonld="${slot}"]`;
  let el = document.head.querySelector(sel);
  if (!payload) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-fg-jsonld", slot);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

function applySiteSeo(s) {
  if (!s) return;
  // 1. Verification meta tags
  injectVerificationMeta("google-site-verification", s.google_site_verification, "google");
  injectVerificationMeta("msvalidate.01", s.bing_site_verification, "bing");
  injectVerificationMeta("p:domain_verify", s.pinterest_site_verification, "pinterest");
  injectVerificationMeta("facebook-domain-verification", s.facebook_domain_verification, "facebook");

  // 2. Organization JSON-LD (with sameAs)
  const sameAs = Array.isArray(s.same_as) ? s.same_as.filter(Boolean) : [];
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: s.brand_name || "Feel Greats",
    url: PROD_DOMAIN,
    logo: s.brand_logo_url || `${PROD_DOMAIN}/og-default.jpg`,
    description: s.brand_tagline || undefined,
    email: s.contact_email || undefined,
    telephone: s.phone || undefined,
    address: (s.address_locality || s.address_region) ? {
      "@type": "PostalAddress",
      addressLocality: s.address_locality || undefined,
      addressRegion: s.address_region || undefined,
      addressCountry: s.address_country || "GB",
    } : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  };
  // Drop undefined keys for clean JSON-LD output
  Object.keys(org).forEach((k) => org[k] === undefined && delete org[k]);
  injectJsonLd("organization", org);

  // 3. WebSite JSON-LD with SearchAction (helps Google show site-search box)
  injectJsonLd("website", {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: s.brand_name || "Feel Greats",
    url: PROD_DOMAIN,
    potentialAction: {
      "@type": "SearchAction",
      target: `${PROD_DOMAIN}/learn?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  });
}

/**
 * Mount once at the App root. Subsequent calls reuse the cached settings.
 */
export default function useSiteSeo() {
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current) return;
    applied.current = true;
    if (_cache) {
      applySiteSeo(_cache);
      return;
    }
    api.get("/seo/site")
      .then((r) => {
        _cache = r.data;
        applySiteSeo(_cache);
      })
      .catch(() => {
        // Non-fatal: pages still render with the per-page useSeo defaults.
      });
  }, []);
}
